import { TableItem } from "./types";
import moment from "moment";
import { isDate, isEmpty, isNumber, round } from "lodash";
import { DropDownCell } from "./DropdownCell";

export const textFields = [
  "category",
  "productName",
  "products",
  "workName",
  "activity",
  "appliedUnit",
  "priceUnit",
  "variety",
  "harvestRateUnit",
  "haulingRateUnit",
  "costUnit",
  "unit",
  "machineName",
  "dryWet",
  "description",
  "type",
  "reasonForCredit",
];

export const dropDownFields = [
  "cropType",
  "fieldType"
]

export const nonEditableFields = [
  "totalCost",
  "totalDue",
  "totalHarvestCost",
  "totalHaulingCost",
];

export const calculationDependentFields = [
  "totalApplied",
  "price",
  "landlordShare",
  "totalAreaHarvested",
  "totalDryYeild",
  "harvestCustomRate",
  "haulingCustomRate",
  "totalAcreTilled",
  "cost",
];

export const adhocTables = ["adhocProducts", "adhocOperations"];

export const creditTables = ["creditTableData"];

export const validateGridData = (
  data: any[],
  headers: { [key: string]: string }
): string[] => {
  const errorFields: string[] = [];
  data.forEach((item) => {
    Object.keys(headers).forEach((field: string) => {
      let inValid = false;
      const value = item[field];
      if (
        field === "id" ||
        textFields.includes(field) ||
        dropDownFields.includes(field)
      ) {
        inValid = isEmpty(value);
      } else if (field === "date") {
        inValid = !isDate(value);
      } else if (!nonEditableFields.includes(field)) {
        inValid = !isNumber(value) || value < 0;
      }

      if (inValid && !errorFields.includes(headers[field])) {
        errorFields.push(headers[field]);
      }
    });
  });
  return errorFields;
};

export const validatePricePeriodsGridData = (
  data: any[],
  headers: { [key: string]: string }
): string[] => {
  const errorFields: string[] = [];
  data.forEach((item) => {
    Object.keys(headers).forEach((field: string) => {
      let inValid = false;
      const value = item[field];
      if (field === "price") {
        inValid = !isNumber(value) || value <= 0;
      } else if (
        field === "firstApplied" ||
        field === "lastApplied" ||
        field === "firstSeeded" ||
        field === "lastSeeded" ||
        field === "firstTilled" ||
        field === "lastTilled" ||
        field === "firstPlanted" ||
        field === "lastPlanted"
      ) {
        inValid = !isDate(value) || value.getFullYear() < 1000;
      } else if (field === "discountedPrice") {
        inValid = !isNumber(value) || value <= 0;
      } else if (field === "seedsPerBag") {
        inValid = isEmpty(value);
      } else if (field === "costPerAcre") {
        inValid = inValid = !isNumber(value) || value <= 0;
      }

      if (inValid && !errorFields.includes(headers[field])) {
        errorFields.push(headers[field]);
      }
    });
  });
  return errorFields;
};

export const validateHarvestPricePeriodsGridData = (data: any[]): string[] => {
  const errorFields: string[] = [];
  data.forEach((item) => {
    const priceInvalid = [
      "harvestPrice",
      "haulingPrice",
      "haulingOveragePrice",
    ].every((field) => !isNumber(item[field]) || item[field] <= 0);
    const datesInvalid = ["firstHarvested", "lastHarvested"].some(
      (field) => !isDate(item[field])
    );
    if (priceInvalid || datesInvalid) {
      errorFields.push("error");
    }
  });
  return errorFields;
};

export const validateContractPricePeriodsGridData = (data: any[]): string[] => {
  const errorFields: string[] = [];
  
  data.forEach((item) => {
    const priceInvalid = ["price", "bushels"].some(
      (field) => !isNumber(item[field]) || item[field] <= 0
    );
    const buyerNameInvalid = !item.buyerName || item.buyerName.trim() === "";
    const dateInvalid = !isDate(item.contractDate);
    
    if (priceInvalid || buyerNameInvalid || dateInvalid) {
      errorFields.push("error");
    }
  });
  
  return errorFields;
};

export const validateAdhocProductData = (
  data: any[],
  headers: { [key: string]: string }
): string[] => {
  const errorFields: string[] = [];
  const requiredFields = [
    "fieldType",
    "cropType",
    "description",
    "landlordShare",
    "totalDue",
  ];
  data.forEach((item) => {
    requiredFields.forEach((field: string) => {
      let inValid = false;
      const value = item[field];
      if (textFields.includes(field) || dropDownFields.includes(field)) {
        inValid = isEmpty(value);
      } else if (field === "date") {
        inValid = !isDate(value);
      } else {
        inValid = !isNumber(value) || value <= 0;
      }
      if(dropDownFields.includes(field)) {
         inValid = isEmpty(value);
      }
      if (inValid && !errorFields.includes(headers[field])) {
        errorFields.push(headers[field]);
      }
    });
  });
  return errorFields;
};

export const validateAdhocOperationsData = (
  data: any[],
  headers: { [key: string]: string }
): string[] => {
  const errorFields: string[] = [];
  const requiredFields = ["date", "description", "landlordShare", "totalDue"];
  data.forEach((item) => {
    requiredFields.forEach((field: string) => {
      let inValid = false;
      const value = item[field];
      if (textFields.includes(field)) {
        inValid = isEmpty(value);
      } else if (field === "date") {
        inValid = !isDate(value);
      } else {
        inValid = !isNumber(value) || value <= 0;
      }

      if (inValid && !errorFields.includes(headers[field])) {
        errorFields.push(headers[field]);
      }
    });
  });
  return errorFields;
};

export const parseTableDate = (items: any[]) => {
  return items.map((item: TableItem) => {
    return { ...item, date: moment(item.date, "MM/DD/YYYY").toDate() };
  });
};

export const formatTableData = (items: any[]) => {
  return items.map((item: TableItem) => {
    return { ...item, date: moment(item.date).format("MM/DD/YYYY") };
  });
};

export const calculateRowTotals = (rowData: any, sectionName: string) => {
  switch (sectionName) {
    case "applicationProductTableData":
    case "applicationOperationTableData":
      rowData["totalCost"] = rowTotalCost(rowData, "totalApplied", "price");
      break;
    case "harvestTableData":
      const totalHarvestCost = rowTotalCost(
        rowData,
        "totalAreaHarvested",
        "harvestCustomRate"
      );
      const totalHaulingCost = rowTotalCost(
        rowData,
        "totalDryYeild",
        "haulingCustomRate"
      );
      rowData["totalHarvestCost"] = totalHarvestCost;
      rowData["totalHaulingCost"] = totalHaulingCost;
      rowData["totalCost"] = totalHarvestCost + totalHaulingCost;
      break;
    case "tillageTableData":
      rowData["totalCost"] = rowTotalCost(rowData, "totalAcreTilled", "cost");
      break;
    case "seedingTableData":
      rowData["totalCost"] = rowTotalCost(rowData, "quantityApplied", "price");
      break;
    case "plantingTableData":
      rowData["totalCost"] = rowTotalCost(rowData, "areaSeeded", "price");
      break;
    default:
      break;
  }
  rowData["totalDue"] = rowTotalDue(rowData);
};

export const rowTotalCost = (
  item: any,
  quantityField: string,
  priceField: string
): number => {
  return round(
    (isNumber(item[quantityField]) ? item[quantityField] : 0) *
      (isNumber(item[priceField]) ? item[priceField] : 0),
    2
  );
};

export const rowTotalDue = (item: any): number => {
  const landlordShare = isNumber(item["landlordShare"])
    ? item["landlordShare"]
    : 0;
  return round((landlordShare * item["totalCost"]) / 100, 2);
};

export const calculateTotalDue = (data: any): number => {
  const totalDue = data.reduce((acc: number, item: any) => {
    const currentTotal = isNumber(item["totalDue"]) ? item["totalDue"] : 0;
    return acc + currentTotal;
  }, 0);
  return round(totalDue, 2);
};

export const newRowData = {
  applicationProductTableData: {
    date: new Date(),
    category: "",
    productName: "",
    workName: "",
    acres: 0,
    acresApplied: 0,
    applicationActivityCost: 0,
    price: 0,
    totalApplied: 0,
    appliedUnit: "",
    priceUnit: "",
    totalCost: 0,
    landlordShare: 0,
    totalDue: 0,
  },
  applicationOperationTableData: {
    date: new Date(),
    activity: "",
    dryWet: "",
    productName: "",
    workName: "",
    machineName: "",
    acres: 0.0,
    price: 0.0,
    totalApplied: "",
    appliedUnit: "",
    priceUnit: "",
    totalCost: 0.0,
    landlordShare: 0,
    totalDue: 0,
  },
  harvestTableData: {
    date: new Date(),
    activity: "",
    variety: "",
    workName: "",
    totalAreaHarvested: 0,
    harvestCustomRate: 0,
    harvestRateUnit: "",
    totalHarvestCost: 0,
    totalDryYeild: 0,
    haulingCustomRate: 0,
    haulingRateUnit: "",
    totalHaulingCost: 0,
    landlordShare: 0,
    totalCost: 0,
    totalDue: 0,
  },
  tillageTableData: {
    date: new Date(),
    activity: "",
    workName: "",
    machineName: "",
    totalAcreTilled: 0,
    unit: "",
    cost: 0,
    costUnit: "",
    totalCost: 0,
    landlordShare: 0,
    totalDue: 0,
  },
  seedingTableData: {
    date: new Date(),
    activity: "",
    variety: "",
    workName: "",
    machineName: "",
    quantityApplied: 0.0,
    price: 0.0,
    unit: "",
    priceUnit: "",
    totalCost: 0.0,
    landlordShare: 0,
    totalDue: 0,
  },
  plantingTableData: {
    date: new Date(),
    activity: "",
    variety: "",
    workName: "",
    machineName: "",
    areaSeeded: 0.0,
    price: 0.0,
    unit: "",
    priceUnit: "",
    totalCost: 0.0,
    landlordShare: 0,
    totalDue: 0,
  },
  adhocProducts: {
    cropType: DropDownCell,
    fieldType: DropDownCell,
    date: new Date(),
    product: "",
    acres: 0,
    totalApplied: 0,
    appliedUnit: "",
    price: 0,
    priceUnit: "",
    totalCost: 0,
    landlordShare: 0,
    totalDue: 0,
  },
  creditTableData: {
    date: new Date(),
    fieldType: "",
    cropType: "",
    activity: "",
    reasonForCredit: "",
    totalDue: 0,
  },
};

export const validateCreditData = (
  data: any[],
  headers: { [key: string]: string }
): string[] => {
  const errorFields: string[] = [];
  data.forEach((item) => {
    Object.keys(headers).forEach((field: string) => {
      if (field === "id") return;
      // fieldType and cropType are optional in credit rows
      if (field === "fieldType" || field === "cropType") return;
      let inValid = false;
      const value = item[field];
      if (field === "date") {
        inValid = !isDate(value);
      } else if (textFields.includes(field)) {
        inValid = isEmpty(value);
      } else if (field === "totalDue") {
        inValid = !isNumber(value);
      }
      if (inValid && !errorFields.includes(headers[field])) {
        errorFields.push(headers[field]);
      }
    });
  });
  return errorFields;
};

export const creditTableHeader: { [key: string]: string } = {
  id: "Id",
  fieldType: "Field",
  cropType: "Crop",
  date: "Date",
  activity: "Activity",
  reasonForCredit: "Reason for Credit",
  totalDue: "Total Adjustments and Credits",
};

export const adhocProductTableHeader = {
  id: "Id",
  fieldType: "Field",
  cropType: "Crop",
  date: "Date",
  description: "Description",
  products: "Products",
  acres: "Farm Acres",
  totalApplied: "Quantity Applied",
  appliedUnit: "Quantity Unit",
  price: "Price",
  priceUnit: "Unit",
  totalCost: "Total Cost",
  landlordShare: "Landlord Share",
  totalDue: "Total Due",
};
